import React, { useEffect, useState } from "react";
import { Table, Select, DatePicker, Button, Tag } from "antd";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { useLazyGetDailyAttendanceQuery, useLazyGetFirstInLastOutQuery } from "../../../services/attendance.service";
import { useLazyGetBranchesQuery } from "../../../services/settings.service";
import dayjs from "dayjs";

const { Option } = Select;

const AttendenceAdmin = () => {
  const [data, setData] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 20 });

  const [getDailyAttendance, { isLoading: isLoadingAttendance }] = useLazyGetDailyAttendanceQuery();
  const [getFirstInLastOut, { isLoading: isLoadingReport }] = useLazyGetFirstInLastOutQuery();
  const [getBranches, { isLoadingBranches }] = useLazyGetBranchesQuery();

  useEffect(() => {
    fetchBranches();
    fetchAttendanceData();
  }, []);

  useEffect(() => {
    fetchAttendanceData();
  }, [selectedDate, selectedBranch, pagination.page]);

  const fetchBranches = async () => {
    try {
      const result = await getBranches({ page: 1, limit: 100 });
      if (result.data?.success) {
        setBranches(result.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchAttendanceData = async () => {
    try {
      const params = {
        date: selectedDate.format('YYYY-MM-DD'),
        page: pagination.page,
        limit: pagination.limit
      };

      if (selectedBranch) {
        params.branch_id = selectedBranch;
      }

      const result = await getDailyAttendance(params);

      if (result.data?.success) {
        setData(result.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date || dayjs());
  };

  const handleBranchChange = (value) => {
    setSelectedBranch(value);
    setPagination({ ...pagination, page: 1 });
  };

  const handleRefresh = () => {
    fetchAttendanceData();
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      'present': { color: 'success', text: 'Present' },
      'absent': { color: 'error', text: 'Absent' },
      'late': { color: 'warning', text: 'Late' },
      'half_day': { color: 'processing', text: 'Half Day' },
      'on_leave': { color: 'default', text: 'On Leave' },
    };

    const config = statusConfig[status?.toLowerCase()] || { color: 'default', text: status || 'Unknown' };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const columns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
      render: (text, record) => (
        <div className="table-avatar">
          <Link to={`/employee-profile/${record.employee_id}`}>
            {record.employee_photo ? (
              <img
                className="avatar avatar-xs"
                src={record.employee_photo}
                alt={text}
              />
            ) : (
              <div className="avatar avatar-xs">
                <span className="avatar-title rounded-circle">
                  {text?.charAt(0)}
                </span>
              </div>
            )}
            <span className="ms-2">{text}</span>
          </Link>
        </div>
      ),
      width: 200,
      fixed: 'left',
    },
    {
      title: "Employee ID",
      dataIndex: "employee_code",
      key: "employee_code",
      width: 120,
    },
    {
      title: "Department",
      dataIndex: "department",
      key: "department",
      width: 150,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
      width: 100,
    },
    {
      title: "First In",
      dataIndex: "first_in",
      key: "first_in",
      render: (time) => time ? dayjs(time).format('hh:mm A') : '-',
      width: 100,
    },
    {
      title: "Last Out",
      dataIndex: "last_out",
      key: "last_out",
      render: (time) => time ? dayjs(time).format('hh:mm A') : '-',
      width: 100,
    },
    {
      title: "Working Hours",
      dataIndex: "working_hours",
      key: "working_hours",
      render: (hours) => hours || '0h 0m',
      width: 120,
    },
    {
      title: "Overtime",
      dataIndex: "overtime_hours",
      key: "overtime_hours",
      render: (hours) => hours || '0h 0m',
      width: 100,
    },
    {
      title: "Late By",
      dataIndex: "late_by",
      key: "late_by",
      render: (time) => time ? <Tag color="warning">{time}</Tag> : '-',
      width: 100,
    },
    {
      title: "Early Leave",
      dataIndex: "early_leave_by",
      key: "early_leave_by",
      render: (time) => time ? <Tag color="warning">{time}</Tag> : '-',
      width: 100,
    },
  ];

  const tableData = data.map((record, index) => ({
    key: record.id || index,
    ...record
  }));

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          {/* Page Header */}
          <Breadcrumbs
            maintitle="Attendance"
            title="Dashboard"
            subtitle="Admin Attendance"
          />
          {/* /Page Header */}

          {/* Filters */}
          <div className="row mb-4">
            <div className="col-md-3">
              <DatePicker
                value={selectedDate}
                onChange={handleDateChange}
                format="DD MMM YYYY"
                style={{ width: '100%' }}
                placeholder="Select Date"
              />
            </div>
            <div className="col-md-3">
              <Select
                placeholder="Select Branch"
                style={{ width: '100%' }}
                onChange={handleBranchChange}
                allowClear
                loading={isLoadingBranches}
              >
                {branches.map(branch => (
                  <Option key={branch.id} value={branch.id}>
                    {branch.name}
                  </Option>
                ))}
              </Select>
            </div>
            <div className="col-md-3">
              <Button
                type="primary"
                onClick={handleRefresh}
                loading={isLoadingAttendance}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Attendance Table */}
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <Table
                    columns={columns}
                    dataSource={tableData}
                    loading={isLoadingAttendance || isLoadingReport}
                    scroll={{ x: 1300 }}
                    pagination={{
                      current: pagination.page,
                      pageSize: pagination.limit,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} records`,
                      onChange: (page, pageSize) => {
                        setPagination({ page, limit: pageSize });
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AttendenceAdmin;
